<script setup lang="ts">
// ================================
// L'AIDE DE L'APPLICATION, DEPUIS L'ÉTUDE (DEV-2311, décision du 05/10/2026).
//
// Ce que le client doit vivre : quand un résultat le surprend, ou qu'une étude
// n'aboutit pas, il sait où demander de l'aide, sans dossier technique à
// copier. La page ne crée aucun canal : elle ouvre la fenêtre du bouton
// « Aide » de l'application (`core/components/support`), par l'événement que le
// menu du profil émet déjà. Aucun délai ni aucune relecture ne sont promis.
//
// Rendu quand la session voit ce bouton (`aideDeLApplicationDisponible`).
// Le client final d'un scope de marque blanche ne l'a pas : depuis le
// 05/10/2026, la page lui dit de contacter son interlocuteur habituel, avec
// l'adresse de contact du scope quand le scope en sert une valide
// (`mail.support`), jamais le nom ni l'adresse de l'éditeur.
// ================================
import { computed } from "vue"
import { useEventBus } from "@vueuse/core"
import { useI18n } from "vue-i18n"
import { SUPPORT_OPEN_EVENT } from "@core/components/support/support.events"
import { useConfigStore } from "@core/stores/config.store"
import {
    adresseDeContactDuScope,
    aideDeLApplicationDisponible,
    estScopeDeMarqueBlanche,
    type ScopeContact,
} from "@modules/courtage/utils/optim-turpe-v2.equipe"

const { t } = useI18n({
    locale: "fr",
    messages: {
        fr: {
            phrase: "Une question sur cette étude, ou un résultat qui vous surprend ? Écrivez-nous depuis l'aide.",
            ouvrir: "Ouvrir l'aide",
            interlocuteur:
                "Une question sur cette étude ? Contactez votre interlocuteur habituel.",
        },
    },
})

/**
 * `compact` : au pied de l'étude allégée (maquette du 07/10/2026), le seul
 * lien « Ouvrir l'aide », sans la phrase (le bouton flottant « Aide » de
 * l'application la porte déjà). Les états d'échec gardent la phrase.
 */
const props = withDefaults(
    defineProps<{
        compact?: boolean
        /**
         * Maquette finale du 15/10/2026 : au pied d'un résultat, « Ouvrir
         * l'aide » double « Comprendre l'étude » et n'est plus écrit ; seule
         * reste, en marque blanche, la phrase de l'interlocuteur habituel.
         */
        interlocuteurSeul?: boolean
    }>(),
    { compact: false, interlocuteurSeul: false }
)

const getAide = computed(() => aideDeLApplicationDisponible())

/** Le scope de la session, lu sans jamais lever (magasin absent : inconnu). */
function scopeCourant(): ScopeContact {
    try {
        return (useConfigStore().scope ?? null) as ScopeContact
    } catch {
        return null
    }
}
/** Client final d'une marque blanche, sans le bouton « Aide ». */
const getInterlocuteur = computed(
    () => !getAide.value && estScopeDeMarqueBlanche(scopeCourant())
)
const getAdresseContact = computed(() =>
    getInterlocuteur.value ? adresseDeContactDuScope(scopeCourant()) : null
)
const busSupportOpen = useEventBus<void>(SUPPORT_OPEN_EVENT)

function onOuvrirAide(): void {
    busSupportOpen.emit()
}
</script>

<template>
    <p
        v-if="getAide && !props.interlocuteurSeul"
        class="optim-turpe-v2-aide text-xs text-primary-600"
        data-testid="optim-turpe-v2-aide-application"
    >
        <template v-if="!props.compact">{{ `${t("phrase")} ` }}</template
        ><button
            type="button"
            class="font-medium text-primary-700 hover:underline"
            data-testid="optim-turpe-v2-aide-application-ouvrir"
            @click="onOuvrirAide"
        >
            {{ t("ouvrir") }}
        </button>
    </p>
    <p
        v-else-if="getInterlocuteur"
        class="optim-turpe-v2-aide text-xs text-primary-600"
        data-testid="optim-turpe-v2-aide-interlocuteur"
    >
        {{ t("interlocuteur") }}
        <a
            v-if="getAdresseContact"
            :href="`mailto:${getAdresseContact}`"
            class="ml-1 select-text font-medium text-primary-700 hover:underline"
            data-testid="optim-turpe-v2-aide-interlocuteur-adresse"
            >{{ getAdresseContact }}</a
        >
    </p>
</template>
